<script lang="ts">
  import { type Warehouse } from '../../lib/types';

  let {
    outsourcedWarehouses,
    enabledIds,
    loadingIds,
    ontoggle,
  }: {
    outsourcedWarehouses: Warehouse[];
    enabledIds: Set<string>;
    loadingIds: Set<string>;
    ontoggle: (warehouseId: string) => void;
  } = $props();
</script>

<div class="outsourced-section">
  <div class="outsourced-header">
    <div class="outsourced-title-group">
      <h4>Third-Party Warehouses</h4>
      <span class="outsourced-subtitle">Enable access to outsourced 3PL fulfillment centers</span>
    </div>
  </div>

  <div class="outsourced-list">
    {#each outsourcedWarehouses as warehouse}
      <div class="outsourced-card" class:enabled={enabledIds.has(warehouse.id)}>
        <div class="outsourced-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 21h18M3 10h18M3 10l9-7 9 7M4 10v11m16-11v11M8 14v3m4-3v3m4-3v3" />
          </svg>
        </div>
        <div class="outsourced-details">
          <span class="outsourced-name">{warehouse.name}</span>
          <span class="outsourced-address">{warehouse.address}</span>
          <span class="outsourced-badge">3PL Partner</span>
        </div>
        <div class="outsourced-action">
          <button
            class="btn-toggle"
            class:active={enabledIds.has(warehouse.id)}
            onclick={() => ontoggle(warehouse.id)}
            disabled={true}
          >
            {#if loadingIds.has(warehouse.id)}
              <span class="toggle-spinner"></span>
            {:else if enabledIds.has(warehouse.id)}
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              Enabled
            {:else}
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
              </svg>
              Enable
            {/if}
          </button>
        </div>
      </div>
    {/each}
  </div>

  <p class="outsourced-note">
    Enabling a third-party warehouse allows you to manage inventory and orders through their fulfillment network.
    Contact sales for custom integrations.
  </p>
</div>

<style>
  .outsourced-section {
    background: linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%);
    border: 1px solid #e0e7ff;
    border-radius: 8px;
    padding: 1.25rem;
    margin-top: 0.5rem;
  }

  .outsourced-header {
    margin-bottom: 1rem;
  }

  .outsourced-title-group h4 {
    margin: 0;
    font-size: 1rem;
    color: #333;
  }

  .outsourced-subtitle {
    font-size: 0.85rem;
    color: #666;
    margin-top: 0.25rem;
    display: block;
  }

  .outsourced-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .outsourced-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 1rem;
    transition: all 0.2s;
  }

  .outsourced-card.enabled {
    border-color: #a5b4fc;
    background: linear-gradient(135deg, #ffffff 0%, #f5f7ff 100%);
  }

  .outsourced-icon {
    width: 44px;
    height: 44px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .outsourced-icon svg {
    width: 22px;
    height: 22px;
    color: white;
  }

  .outsourced-details {
    flex: 1;
    min-width: 0;
  }

  .outsourced-name {
    display: block;
    font-weight: 600;
    color: #333;
    font-size: 1rem;
  }

  .outsourced-address {
    display: block;
    font-size: 0.85rem;
    color: #666;
    margin-top: 0.125rem;
  }

  .outsourced-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    font-size: 0.7rem;
    font-weight: 600;
    border-radius: 4px;
    margin-top: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .outsourced-action {
    flex-shrink: 0;
  }

  .btn-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid #ddd;
    background: white;
    color: #667eea;
    font-size: 0.85rem;
    min-width: 100px;
    justify-content: center;
  }

  .btn-toggle:hover:not(:disabled) {
    border-color: #667eea;
    background: #f5f7ff;
  }

  .btn-toggle.active {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-color: transparent;
  }

  .btn-toggle.active:hover:not(:disabled) {
    opacity: 0.9;
  }

  .btn-toggle:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-toggle svg {
    width: 16px;
    height: 16px;
  }

  .toggle-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #e5e7eb;
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .btn-toggle.active .toggle-spinner {
    border-color: rgba(255, 255, 255, 0.3);
    border-top-color: white;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .outsourced-note {
    margin: 1rem 0 0;
    font-size: 0.8rem;
    color: #666;
    text-align: center;
  }

  @media (max-width: 640px) {
    .outsourced-card {
      flex-wrap: wrap;
    }

    .outsourced-action {
      width: 100%;
      margin-top: 0.75rem;
      padding-top: 0.75rem;
      border-top: 1px solid #e5e7eb;
    }

    .btn-toggle {
      width: 100%;
    }
  }
</style>
